import { httpApi } from '@/api/http.api'

export type ServiceOrderKindDto = 'INSPECTION' | 'REPAIR' | 'OTHER'

export type ServiceOrderStatusDto = 'PENDING' | 'IN_PROGRESS' | 'DONE'

export type ServiceOrderOutcomeDto = 'CANCELLED' | 'COMPLETED' | 'ACCEPTED' | 'REJECTED'

export type ServiceOrderDocumentSourceDto = 'TEMPLATE' | 'UPLOAD'

/** Plik z dysku albo zdjęcie z aparatu telefonu. */
export type ServiceOrderAttachmentSourceDto = 'FILE' | 'CAMERA'

export type ServiceOrderItemDto = {
  id: string
  activityId: string | null
  label: string
  position: number
  done: boolean
  /** Kody wykonanych czynności 1–8, rosnąco. */
  codes: number[]
}

export type ServiceOrderSummaryDto = {
  id: string
  orderNumber: string
  kind: ServiceOrderKindDto
  status: ServiceOrderStatusDto
  description: string
  neededAction: string
  orderedAt: string
  expectedAt: string
  position: number
  deviceId: string | null
  deviceName: string
  deviceType: string
  /** Pusty, gdy urządzenie usunięto albo zlecenie nie wskazuje urządzenia z systemu. */
  device: { serialNumber: string } | null
  deviceAddress: string | null
  firmId: string | null
  clientName: string | null
  clientAddress: string | null
  clientOrderingParty: string | null
  clientContact: string | null
  assignee: { id: string; firstName: string | null; lastName: string | null } | null
  items: ServiceOrderItemDto[]
  archivedAt: string | null
  outcome: ServiceOrderOutcomeDto | null
  cancelReason: string | null
  createdAt: string
  updatedAt: string
}

export type ServiceOrderDocumentDto = {
  id: string
  source: ServiceOrderDocumentSourceDto
  name: string
  type: string
  size: number
  templateId: string | null
  addedAt: string
  addedById: string | null
}

export type ServiceOrderStatusChangeDto = {
  id: string
  /** `null` przy pierwszym wpisie — utworzeniu zlecenia. */
  fromStatus: ServiceOrderStatusDto | null
  toStatus: ServiceOrderStatusDto
  changedAt: string
  changedBy: { id: string; firstName: string | null; lastName: string | null } | null
}

/** Załącznik czynności bez treści — plik pobiera się osobno. */
export type ServiceOrderAttachmentDto = {
  id: string
  source: ServiceOrderAttachmentSourceDto
  name: string
  type: string
  size: number
  addedAt: string
  addedById: string | null
}

/** Pozycja w szczegółach z wynikiem pracy serwisanta; `done` wynika z wybrania co najmniej jednego kodu. */
export type ServiceOrderItemDetailsDto = ServiceOrderItemDto & {
  note: string
  attachments: ServiceOrderAttachmentDto[]
}

/** Część raportu wypełniana przez serwisanta; `null` w odpowiedzi = brak odpowiedzi. */
export type ServiceOrderReportDto = {
  /** Pierwszy zapis raportu — rozpoczęcie realizacji. */
  openedAt: string
  /** Zakończenie zlecenia; od tej chwili raportu serwisanta nie da się zmienić. */
  finishedAt: string | null
  executedBy: { id: string; firstName: string | null; lastName: string | null } | null
  serviceBookEntry: boolean | null
  operationApproval: boolean | null
  udtExamination: boolean | null
  /** Podpis klienta jako data URL obrazka. */
  signature: string | null
  signedByFirstName: string | null
  signedByLastName: string | null
  updatedAt: string
}

export type ServiceModeDto = 'STANDARD' | 'EXPRESS'

/** Rozliczenie kierownika; `null` w trybie i płatności = jeszcze bez wyboru. */
export type ServiceOrderSettlementDto = {
  travelKm: number
  people: number
  workHours: number
  mode: ServiceModeDto | null
  paidService: boolean | null
  /** Wersja do odesłania w `settlementUpdatedAt` przy zapisie i akceptacji. */
  updatedAt: string
}

export type ServiceOrderDto = Omit<ServiceOrderSummaryDto, 'items'> & {
  items: ServiceOrderItemDetailsDto[]
  /** Powiązania są wzajemne — lista obejmuje też zlecenia, które wskazały to zlecenie. */
  relatedOrders: { id: string; orderNumber: string; deviceName: string; description: string }[]
  documents: ServiceOrderDocumentDto[]
  /** Historia statusu od utworzenia, od najstarszej zmiany. */
  statusChanges: ServiceOrderStatusChangeDto[]
  /** `null`, dopóki serwisant nie zapisał raportu. */
  report: ServiceOrderReportDto | null
  /** Tylko w odpowiedziach dla kierownika (serwisant nie dostaje pola); `null` razem z raportem. */
  settlement?: ServiceOrderSettlementDto | null
}

/** Liczby nieujemne (`people` całkowita); tryb i płatność mogą zostać puste w zapisie, akceptacja wymaga obu. */
export type ServiceOrderSettlementRequest = {
  /** `settlement.updatedAt` z ostatnio pobranych szczegółów; nowszy zapis w bazie daje 409 i nic nie nadpisuje. */
  settlementUpdatedAt: string
  travelKm: number
  people: number
  workHours: number
  mode?: ServiceModeDto | null
  paidService?: boolean | null
}

export type ServiceOrderReportRequest = {
  /**
   * `report.updatedAt` z ostatnio pobranych szczegółów albo `null`, gdy raportu jeszcze nie było.
   * Nowszy raport w bazie (inna karta, spóźniony zapis) daje 409 i nic nie nadpisuje.
   */
  reportUpdatedAt: string | null
  /** Tylko wysłane czynności się zmieniają; czynność usunięta w międzyczasie przez kierownika daje 409. */
  items: { itemId: string; note: string; codes: number[] }[]
  serviceBookEntry?: boolean | null
  operationApproval?: boolean | null
  udtExamination?: boolean | null
  /** Podpis klienta jako data URL (PNG, JPEG albo WebP, do 512 KB); brak pola czyści zapisany podpis. */
  signature?: string | null
  signedByFirstName?: string | null
  signedByLastName?: string | null
}

export type ServiceOrderDocumentContentDto = ServiceOrderDocumentDto & {
  /** Plik jako data URL. */
  content: string
}

export type ServiceOrderAttachmentContentDto = ServiceOrderAttachmentDto & {
  /** Plik jako data URL. */
  content: string
}

/** JPEG, PNG, WebP, HEIC/HEIF albo PDF; do 10 MB na plik, do 20 plików i 25 MB na całe zlecenie. */
export type ServiceOrderAttachmentRequest = {
  source: ServiceOrderAttachmentSourceDto
  name: string
  /** Plik jako data URL. */
  content: string
}

export type ServiceOrderSummaryPageDto = {
  data: ServiceOrderSummaryDto[]
  total: number
  page: number
  limit: number
}

export type ServiceTechnicianDto = {
  id: string
  firstName: string | null
  lastName: string | null
}

export type ServiceDeviceFirmAddressDto = {
  firmId: string
  firmName: string
  address: string
}

/** Urządzenie do wyboru w formularzu zlecenia; `category` (rodzaj) dobiera czynności z katalogu. */
export type ServiceOrderDeviceDto = {
  id: string
  name: string
  serialNumber: string
  type: string
  category: string | null
  /** Firmy urządzenia z adresem (wg nazwy) do przycisku „Pobierz z danych firmy”; pusta, gdy żadna nie ma adresu. */
  firmAddresses: ServiceDeviceFirmAddressDto[]
}

/** Plik dokumentacji szablonu urządzenia bez treści — do zlecenia dodaje się go przez `templateId` i nazwę. */
export type ServiceOrderDeviceDocumentDto = {
  templateId: string
  name: string
  type: string | null
}

export type ServiceOrderRequest = {
  kind: ServiceOrderKindDto
  description: string
  neededAction?: string
  /** Data w formacie RRRR-MM-DD. */
  expectedAt: string
  deviceId?: string
  deviceName: string
  deviceType: string
  deviceAddress?: string
  firmId?: string
  clientName?: string
  clientAddress?: string
  clientOrderingParty?: string
  clientContact?: string
  /** Czynności z katalogu w kolejności na zleceniu. */
  activityIds: string[]
  relatedOrderIds?: string[]
}

export type ServiceOrderCreateRequest = ServiceOrderRequest & {
  /** Zgłoszenie klienta zamieniane w to zlecenie; zamknięte w międzyczasie kończy się błędem 409. */
  serviceRequestId?: string
}

export type ServiceOrderUpdateRequest = ServiceOrderRequest & {
  /** `updatedAt` ze szczegółów, które otworzył formularz — inny oznacza cudzy zapis w międzyczasie (409). */
  updatedAt: string
}

export type ServiceOrdersQuery = {
  search?: string
  /** Id serwisanta albo 'unassigned'. */
  assigneeId?: string
  clientName?: string
  deviceType?: string
  /** Zakres daty zlecenia, RRRR-MM-DD. */
  orderedFrom?: string
  orderedTo?: string
}

export type ServiceOrderDocumentRequest =
  | { source: 'TEMPLATE'; templateId: string; name: string }
  | { source: 'UPLOAD'; name: string; /** Plik jako data URL. */ content: string }

const SERVICE_ORDERS = 'service-orders'

export const getServiceOrders = (query: ServiceOrdersQuery = {}): Promise<ServiceOrderSummaryDto[]> =>
  httpApi.get<ServiceOrderSummaryDto[]>(SERVICE_ORDERS, { params: query }).then(({ data }) => data)

export const getServiceOrder = (id: string): Promise<ServiceOrderDto> =>
  httpApi.get<ServiceOrderDto>(`${SERVICE_ORDERS}/${id}`).then(({ data }) => data)

export const createServiceOrder = (payload: ServiceOrderCreateRequest): Promise<ServiceOrderDto> =>
  httpApi.post<ServiceOrderDto>(SERVICE_ORDERS, payload).then(({ data }) => data)

export const updateServiceOrder = (id: string, payload: ServiceOrderUpdateRequest): Promise<ServiceOrderDto> =>
  httpApi.put<ServiceOrderDto>(`${SERVICE_ORDERS}/${id}`, payload).then(({ data }) => data)

/** Przestawia kartę w obrębie jej kolumny; `beforeId: null` oznacza koniec kolumny. */
export const moveServiceOrder = (id: string, beforeId: string | null): Promise<void> =>
  httpApi.patch<void>(`${SERVICE_ORDERS}/${id}/position`, { beforeId }).then(({ data }) => data)

/** Aktywne konta z rolą serwisanta, które można przypisać do zlecenia. */
export const getServiceTechnicians = (): Promise<ServiceTechnicianDto[]> =>
  httpApi.get<ServiceTechnicianDto[]>(`${SERVICE_ORDERS}/technicians`).then(({ data }) => data)

export const getServiceOrderDevices = (): Promise<ServiceOrderDeviceDto[]> =>
  httpApi.get<ServiceOrderDeviceDto[]>(`${SERVICE_ORDERS}/devices`).then(({ data }) => data)

/** `language` (np. PL, EN): przy tej samej nazwie pliku w kilku szablonach wygrywa szablon w tym języku. */
export const getServiceOrderDeviceDocuments = (
  deviceId: string,
  language?: string,
): Promise<ServiceOrderDeviceDocumentDto[]> =>
  httpApi
    .get<ServiceOrderDeviceDocumentDto[]>(`${SERVICE_ORDERS}/devices/${deviceId}/documents`, {
      params: language ? { language } : undefined,
    })
    .then(({ data }) => data)

/**
 * Przypisuje serwisanta albo odpina go (`null`). Zmiana możliwa w zleceniu oczekującym i w realizacji;
 * zlecenia w realizacji nie można zostawić bez serwisanta, a zakończonego — przepisać (409).
 */
export const assignServiceOrder = (id: string, assigneeId: string | null): Promise<ServiceOrderDto> =>
  httpApi.patch<ServiceOrderDto>(`${SERVICE_ORDERS}/${id}/assignee`, { assigneeId }).then(({ data }) => data)

/** Anuluje zlecenie, którego serwisant nie zakończył; powód jest wymagany. Zlecenie trafia do archiwum. */
export const cancelServiceOrder = (id: string, reason: string): Promise<ServiceOrderDto> =>
  httpApi.post<ServiceOrderDto>(`${SERVICE_ORDERS}/${id}/cancel`, { reason }).then(({ data }) => data)

/** Archiwizuje zakończone zlecenie z wynikiem „zakończone”. */
export const archiveServiceOrder = (id: string): Promise<ServiceOrderDto> =>
  httpApi.post<ServiceOrderDto>(`${SERVICE_ORDERS}/${id}/archive`).then(({ data }) => data)

/** Zapisuje rozliczenie zlecenia zakończonego przez serwisanta; przed zakończeniem i w archiwum — 409. */
export const saveServiceOrderSettlement = (
  id: string,
  payload: ServiceOrderSettlementRequest,
): Promise<ServiceOrderDto> =>
  httpApi.put<ServiceOrderDto>(`${SERVICE_ORDERS}/${id}/settlement`, payload).then(({ data }) => data)

/**
 * Zapisuje rozliczenie i przenosi zakończone zlecenie do archiwum z wynikiem „zaakceptowane”.
 * Bez trybu usługi albo odpowiedzi o płatności — 400 i nic się nie zapisuje.
 */
export const acceptServiceOrder = (id: string, payload: ServiceOrderSettlementRequest): Promise<ServiceOrderDto> =>
  httpApi.post<ServiceOrderDto>(`${SERVICE_ORDERS}/${id}/accept`, payload).then(({ data }) => data)

export const getServiceOrderDocument = (id: string, documentId: string): Promise<ServiceOrderDocumentContentDto> =>
  httpApi
    .get<ServiceOrderDocumentContentDto>(`${SERVICE_ORDERS}/${id}/documents/${documentId}`)
    .then(({ data }) => data)

export const addServiceOrderDocument = (id: string, payload: ServiceOrderDocumentRequest): Promise<ServiceOrderDto> =>
  httpApi.post<ServiceOrderDto>(`${SERVICE_ORDERS}/${id}/documents`, payload).then(({ data }) => data)

export const removeServiceOrderDocument = (id: string, documentId: string): Promise<void> =>
  httpApi.delete<void>(`${SERVICE_ORDERS}/${id}/documents/${documentId}`).then(({ data }) => data)

/** Podgląd załącznika z raportu serwisanta. */
export const getServiceOrderAttachment = (
  id: string,
  itemId: string,
  attachmentId: string,
): Promise<ServiceOrderAttachmentContentDto> =>
  httpApi
    .get<ServiceOrderAttachmentContentDto>(`${SERVICE_ORDERS}/${id}/items/${itemId}/attachments/${attachmentId}`)
    .then(({ data }) => data)

/**
 * Zlecenia zalogowanego serwisanta — tylko przypisane do niego; cudze zwracają 404.
 * Zarchiwizowane można tylko odczytać, zapisy przyjmuje wyłącznie zlecenie otwarte.
 */
const ASSIGNED_SERVICE_ORDERS = 'assigned-service-orders'

/** Praca do wykonania: zlecenia oczekujące i w realizacji, od najbliższego terminu. */
export const getAssignedServiceOrders = (): Promise<ServiceOrderSummaryDto[]> =>
  httpApi.get<ServiceOrderSummaryDto[]>(ASSIGNED_SERVICE_ORDERS).then(({ data }) => data)

/**
 * Zakładka „Zakończone”: zakończone zlecenia serwisanta (na górze, czekają na kierownika) i jego zlecenia
 * z archiwum z wynikiem, także anulowane. Domyślnie strona 1 po 50, najwyżej 100.
 */
export const getCompletedAssignedServiceOrders = (
  query: { page?: number; limit?: number } = {},
): Promise<ServiceOrderSummaryPageDto> =>
  httpApi
    .get<ServiceOrderSummaryPageDto>(`${ASSIGNED_SERVICE_ORDERS}/completed`, { params: query })
    .then(({ data }) => data)

export const getAssignedServiceOrder = (id: string): Promise<ServiceOrderDto> =>
  httpApi.get<ServiceOrderDto>(`${ASSIGNED_SERVICE_ORDERS}/${id}`).then(({ data }) => data)

export const getAssignedServiceOrderDocument = (
  id: string,
  documentId: string,
): Promise<ServiceOrderDocumentContentDto> =>
  httpApi
    .get<ServiceOrderDocumentContentDto>(`${ASSIGNED_SERVICE_ORDERS}/${id}/documents/${documentId}`)
    .then(({ data }) => data)

/**
 * Zapisuje szkic raportu. Pierwszy zapis zmienia status zlecenia na „w realizacji”;
 * zakończonego zlecenia nie da się już zapisać (409). Odpowiedź niesie nowe `report.updatedAt` do kolejnego zapisu.
 */
export const saveAssignedServiceOrderReport = (
  id: string,
  payload: ServiceOrderReportRequest,
): Promise<ServiceOrderDto> =>
  httpApi.put<ServiceOrderDto>(`${ASSIGNED_SERVICE_ORDERS}/${id}/report`, payload).then(({ data }) => data)

/**
 * Zapisuje raport i kończy zlecenie. Wymaga kodu przy każdej czynności, odpowiedzi na trzy pytania, podpisu
 * oraz imienia i nazwiska podpisującego — brak daje 400 z kluczem nazywającym brakującą część, nic nie zapisane.
 */
export const completeAssignedServiceOrder = (
  id: string,
  payload: ServiceOrderReportRequest,
): Promise<ServiceOrderDto> =>
  httpApi.post<ServiceOrderDto>(`${ASSIGNED_SERVICE_ORDERS}/${id}/complete`, payload).then(({ data }) => data)

/** Wycofuje omyłkowe rozpoczęcie (w realizacji → oczekuje); zapisany szkic raportu zostaje. */
export const revertAssignedServiceOrder = (id: string): Promise<ServiceOrderDto> =>
  httpApi.post<ServiceOrderDto>(`${ASSIGNED_SERVICE_ORDERS}/${id}/revert`).then(({ data }) => data)

export const getAssignedServiceOrderAttachment = (
  id: string,
  itemId: string,
  attachmentId: string,
): Promise<ServiceOrderAttachmentContentDto> =>
  httpApi
    .get<ServiceOrderAttachmentContentDto>(
      `${ASSIGNED_SERVICE_ORDERS}/${id}/items/${itemId}/attachments/${attachmentId}`,
    )
    .then(({ data }) => data)

/**
 * Dołącza plik do czynności; jak zapis raportu rozpoczyna realizację zlecenia oczekującego. Nie zmienia
 * `report.updatedAt` istniejącego raportu — przy pierwszym załączniku raport powstaje, więc wersję bierz z odpowiedzi.
 */
export const addAssignedServiceOrderAttachment = (
  id: string,
  itemId: string,
  payload: ServiceOrderAttachmentRequest,
): Promise<ServiceOrderDto> =>
  httpApi
    .post<ServiceOrderDto>(`${ASSIGNED_SERVICE_ORDERS}/${id}/items/${itemId}/attachments`, payload)
    .then(({ data }) => data)

export const removeAssignedServiceOrderAttachment = (
  id: string,
  itemId: string,
  attachmentId: string,
): Promise<ServiceOrderDto> =>
  httpApi
    .delete<ServiceOrderDto>(`${ASSIGNED_SERVICE_ORDERS}/${id}/items/${itemId}/attachments/${attachmentId}`)
    .then(({ data }) => data)

/**
 * Podgląd zlecenia powiązanego z przypisanym zleceniem `id` — tylko odczyt, bez rozliczenia.
 * Zlecenie niepowiązane daje 404 jak cudze.
 */
export const getAssignedRelatedServiceOrder = (id: string, relatedId: string): Promise<ServiceOrderDto> =>
  httpApi.get<ServiceOrderDto>(`${ASSIGNED_SERVICE_ORDERS}/${id}/related/${relatedId}`).then(({ data }) => data)

export const getAssignedRelatedServiceOrderDocument = (
  id: string,
  relatedId: string,
  documentId: string,
): Promise<ServiceOrderDocumentContentDto> =>
  httpApi
    .get<ServiceOrderDocumentContentDto>(
      `${ASSIGNED_SERVICE_ORDERS}/${id}/related/${relatedId}/documents/${documentId}`,
    )
    .then(({ data }) => data)

export const getAssignedRelatedServiceOrderAttachment = (
  id: string,
  relatedId: string,
  itemId: string,
  attachmentId: string,
): Promise<ServiceOrderAttachmentContentDto> =>
  httpApi
    .get<ServiceOrderAttachmentContentDto>(
      `${ASSIGNED_SERVICE_ORDERS}/${id}/related/${relatedId}/items/${itemId}/attachments/${attachmentId}`,
    )
    .then(({ data }) => data)
